import type { ClientPositionView } from "@solomon-apps/shared";
import { YaasPositionStatus } from "@solomon-apps/shared";
import { lpProtocolLabel } from "@solomon-apps/shared/src/yaas/lpProtocols";
import { BigDecimal } from "@solomon-labs/bigdecimal";

import { isLpPosition, portfolioHoldingIdentity, sumPositionAmounts, toUsdv } from "@/hooks/positionAmounts";
import { resolveProgramSurfaceType, shouldFetchLiveUsdvBalance } from "@/hooks/positionDisplayAmount";

import { programSurfaceTypeLabel } from "../shared/format";

import { positionRewardRate } from "./positionRewardMetrics";

/**
 * Holdings — what the client actually owns, with Programs abstracted away.
 *
 * yaas-api creates one Program Position per (Program, wallet), so a wallet
 * targeted by several Programs comes back as several near-identical position
 * records reporting the same funds. The client console never shows Programs:
 * every position-per-row surface groups those records into ONE holding.
 *
 * Grouping key (`holdingKey`):
 *   - Address surfaces (live on-chain USDv): one holding per balance address
 *     (the Squads vault for multisigs, else the wallet address).
 *   - LP positions: one holding per pool a wallet is in (owner + protocol +
 *     pool + asset mint). A wallet's several LP positions in one pool (each
 *     its own NFT and range) read as one: for the USDv it holds there, which
 *     position is which does not matter. Their balances sum, each distinct
 *     LP position (`portfolioHoldingIdentity`) counted once however many
 *     Programs it sits under.
 *   - Anything else (no physical identity to prove two records are the same
 *     funds): one holding per position record.
 */

export type HoldingKind = "address" | "lp" | "position";

export interface Holding {
    /** Stable grouping identity. Never displayed. */
    key: string;
    kind: HoldingKind;
    /**
     * The underlying position uuid the holding routes by (`/positions/:uuid`):
     * the earliest-created record, so links stay stable as Programs come and go.
     */
    primaryUuid: string;
    /** Every underlying Program Position uuid. For actions/routing, never displayed. */
    positionUuids: string[];
    /** Underlying records, earliest-created first. For actions, never displayed. */
    positions: ClientPositionView[];
    /** Client-facing name. Never a Program or per-Program surface name. */
    name: string;
    /** Surface category, e.g. "Address", "Raydium CLMM" or "Raydium CPMM". */
    surface: string;
    /** Where the USDv lives: Squads vault for multisigs, else the wallet. */
    address: string;
    /** USDv in the holding, counted ONCE. Null while unknown. */
    balance: BigDecimal | null;
    balanceLoading: boolean;
    /** Σ lifetime rewards across the underlying positions. */
    rewardsEarned: BigDecimal;
    /** Σ pending rewards; null until every underlying position reports one. */
    rewardsPending: BigDecimal | null;
    status: YaasPositionStatus;
    /** The underlying record whose status the holding shows (reasons, review mode). */
    statusSource: ClientPositionView;
    /** The wallet's rewards destination. */
    rewardsDestinationAddress: string | null;
}

/** Per-position balance evidence, as `usePositionBalances` resolves it. */
export interface HoldingBalanceInput {
    amount: BigDecimal | null;
    isLoading?: boolean;
    shouldFetchLiveUsdv: boolean;
}

export interface GroupHoldingsOptions {
    /** Resolved balances keyed by position uuid. */
    balances?: ReadonlyMap<string, HoldingBalanceInput>;
    /** Program Surface type from the catalog when the payload omits it. */
    resolvedTypeOf?: (position: ClientPositionView) => string | null;
}

/**
 * Holding status order, most to least relevant. Active wins (the holding is
 * earning); otherwise the most actionable remaining state: Pending (in
 * review), Suspended and Rejected (both can be resubmitted), then the
 * terminal Disabled / Hidden.
 */
export const HOLDING_STATUS_ORDER: readonly YaasPositionStatus[] = [
    YaasPositionStatus.Active,
    YaasPositionStatus.Pending,
    YaasPositionStatus.Suspended,
    YaasPositionStatus.Rejected,
    YaasPositionStatus.Disabled,
    YaasPositionStatus.Hidden
];

export function holdingStatus(statuses: readonly YaasPositionStatus[]): YaasPositionStatus {
    for (const status of HOLDING_STATUS_ORDER) {
        if (statuses.includes(status)) return status;
    }
    return statuses[0] ?? YaasPositionStatus.Pending;
}

/**
 * The holding's USDv, shown ONCE. The underlying records report the same
 * physical funds, so they are never summed.
 *
 * - Any unknown evidence (still loading, unreadable, incomplete LP
 *   observation) leaves the holding unknown rather than guessing.
 * - Address records share one live balance query, so they always agree.
 * - LP records can disagree when their observations were taken at different
 *   times. That is timing skew, not extra principal, so take the max: it never
 *   double counts and never under-reports funds the client verifiably holds.
 */
export function holdingBalance(amounts: readonly (BigDecimal | null)[]): BigDecimal | null {
    let max: BigDecimal | null = null;
    for (const amount of amounts) {
        if (amount === null) return null;
        if (max === null || amount.gt(max)) max = amount;
    }
    return max;
}

/**
 * An LP pool holding's USDv: the sum of its distinct LP positions, each one
 * `holdingBalance` of its own records (the same position under several
 * Programs, counted once). A position whose observation is not complete is
 * left out rather than blanking the pool; the pool is unknown only when no
 * position in it is known.
 */
export function poolHoldingBalance(positions: readonly (readonly (BigDecimal | null)[])[]): BigDecimal | null {
    let sum: BigDecimal | null = null;
    for (const amounts of positions) {
        const amount = holdingBalance(amounts);
        if (amount !== null) sum = sum === null ? amount : sum.add(amount);
    }
    return sum;
}

/**
 * Which holding a position belongs to: an LP position by its pool (owner,
 * protocol, pool, asset mint), so a wallet's positions in one pool share a
 * row; anything else by the funds it is (`portfolioHoldingIdentity`).
 */
export function holdingKey(
    position: ClientPositionView,
    balance: Parameters<typeof portfolioHoldingIdentity>[1]
): string {
    const lp = position.lpPosition;
    if (lp) {
        return JSON.stringify(["lp-pool", lp.holdingOwnerAddress, lp.protocol, lp.poolAddress, lp.observedAssetMint]);
    }
    return portfolioHoldingIdentity(position, balance);
}

/** Computed USDv entitlement that has not yet been paid. */
export function positionPendingRewards(position: ClientPositionView): BigDecimal | null {
    const value = position.rewardMetrics?.pendingRewards;
    return value === null || value === undefined ? null : new BigDecimal(value);
}

/** All routes in a holding. The backend stores each position separately. */
export function holdingDestinationAddresses(positions: readonly ClientPositionView[]): string[] {
    return [
        ...new Set(
            positions.map(
                (position) =>
                    position.rewardsDestinationAddress || position.usdvBalanceAddress || position.account.address
            )
        )
    ];
}

/** A single address only when every position has the same effective route. */
export function holdingRewardsDestination(positions: readonly ClientPositionView[]): string | null {
    const destinations = holdingDestinationAddresses(positions);
    return destinations.length === 1 ? destinations[0] : null;
}

/** Paid rewards divided by settlement balance exposure, annualized. */
export function holdingRealizedApy(holding: Holding): number | null {
    const rate = positionRewardRate([holding.positions], "paid");
    return rate === null ? null : Number(rate.toString());
}

export function holdingApyDisplay(holding: Holding): string {
    const rate = positionRewardRate([holding.positions], "paid");
    return rate === null ? "—" : `${rate.round(2).toFixed(2, true)}%`;
}

/**
 * Surface category for a position: an LP's protocol label (e.g. "Raydium CPMM"),
 * else the payload's surface label when present (e.g. "Meteora DAMM"), else a
 * label for the Program Surface type. Never the per-Program surface name.
 * An LP row the API could not resolve sends only the pool kind ("CLMM") as its
 * surface label, so a Raydium row is named from its type slug instead.
 */
export function positionSurfaceLabel(p: ClientPositionView, resolvedType: string | null): string {
    if (p.lpPosition) return lpProtocolLabel(p.lpPosition.protocol);
    if (/raydium/i.test(p.programSurface.type)) return lpProtocolLabel(p.programSurface.type);
    if (p.programSurface.surface?.trim()) return p.programSurface.surface;
    if (resolvedType === "ADDRESS" || resolvedType === "TREASURY") return "Address";
    if (resolvedType) {
        const typeLabel = programSurfaceTypeLabel(resolvedType);
        if (typeLabel.trim()) return typeLabel;
    }
    return "—";
}

/** The one product every underlying record agrees on, else null. */
function sharedProduct(positions: readonly ClientPositionView[]): string | null {
    const products = new Set(positions.map((p) => p.programSurface.product?.trim() ?? ""));
    if (products.size !== 1) return null;
    const [product] = [...products];
    return product ? product : null;
}

/**
 * Client-facing holding name, matching Add Position's holdings preview:
 * Address holdings are "USDv" (the asset they hold; the Surface column says
 * "Address"); LP holdings use the pool pair yaas-api names from Jachin's
 * catalog (e.g. "USDC / USDv"), else the instrument the records agree on,
 * else the surface. The Program Surface `name` is never used: it is
 * per-Program ("Solana Wallet Address").
 */
function holdingName(kind: HoldingKind, positions: readonly ClientPositionView[], surface: string): string {
    if (kind === "address") return "USDv";
    const pairLabel = positions.find((p) => p.lpPosition?.pairLabel)?.lpPosition?.pairLabel;
    return pairLabel ?? sharedProduct(positions) ?? surface;
}

/** A pool holding's amounts, one list per distinct LP position in it. */
function byLpPosition(
    positions: readonly ClientPositionView[],
    amounts: readonly (BigDecimal | null)[]
): (BigDecimal | null)[][] {
    const groups = new Map<string, (BigDecimal | null)[]>();
    positions.forEach((p, index) => {
        const identity = portfolioHoldingIdentity(p, undefined);
        groups.set(identity, [...(groups.get(identity) ?? []), amounts[index]]);
    });
    return [...groups.values()];
}

function byCreated(a: ClientPositionView, b: ClientPositionView): number {
    const at = Date.parse(a.createdAt) || 0;
    const bt = Date.parse(b.createdAt) || 0;
    return at - bt || a.uuid.localeCompare(b.uuid);
}

/**
 * Group Program Positions into holdings. Order follows each holding's first
 * appearance in `positions`. Pure: balances and catalog types are passed in.
 */
export function groupHoldings(positions: readonly ClientPositionView[], options: GroupHoldingsOptions = {}): Holding[] {
    const { balances, resolvedTypeOf } = options;
    const groups = new Map<string, { kind: HoldingKind; positions: ClientPositionView[] }>();
    for (const p of positions) {
        const balance = balances?.get(p.uuid);
        const direct =
            balance?.shouldFetchLiveUsdv ??
            (!isLpPosition(p) &&
                shouldFetchLiveUsdvBalance(p.programSurface.type, resolvedTypeOf?.(p), p.programSurface.surface));
        const key = holdingKey(p, { amount: balance?.amount ?? null, shouldFetchLiveUsdv: direct });
        const kind: HoldingKind = p.lpPosition ? "lp" : direct ? "address" : "position";
        const group = groups.get(key);
        if (group) group.positions.push(p);
        else groups.set(key, { kind, positions: [p] });
    }

    return [...groups.entries()].map(([key, { kind, positions: group }]) => {
        const sorted = [...group].sort(byCreated);
        const first = sorted[0];
        const surface =
            kind === "address"
                ? "Address"
                : positionSurfaceLabel(
                      first,
                      resolveProgramSurfaceType(first.programSurface.type, resolvedTypeOf?.(first))
                  );
        const status = holdingStatus(sorted.map((p) => p.status));
        const evidence = sorted.map((p) => balances?.get(p.uuid));
        const amounts = evidence.map((b) => b?.amount ?? null);
        return {
            key,
            kind,
            primaryUuid: first.uuid,
            positionUuids: sorted.map((p) => p.uuid),
            positions: sorted,
            name: holdingName(kind, sorted, surface),
            surface,
            address: first.usdvBalanceAddress ?? first.account.address,
            balance: kind === "lp" ? poolHoldingBalance(byLpPosition(sorted, amounts)) : holdingBalance(amounts),
            balanceLoading: evidence.some((b) => Boolean(b?.isLoading)),
            rewardsEarned: sorted.reduce((sum, p) => sum.add(toUsdv(p.totalRewards)), new BigDecimal(0)),
            rewardsPending: sumPositionAmounts(sorted.map(positionPendingRewards)),
            status,
            statusSource: sorted.find((p) => p.status === status) ?? first,
            rewardsDestinationAddress: holdingRewardsDestination(sorted)
        };
    });
}

/** The holding that contains a position uuid — keeps `/positions/:uuid` links stable. */
export function findHolding(holdings: readonly Holding[], positionUuid: string): Holding | null {
    return holdings.find((h) => h.positionUuids.includes(positionUuid)) ?? null;
}

/** Σ holding balances, each counted once; null while any is unknown. */
export function sumHoldingBalances(holdings: readonly Holding[]): BigDecimal | null {
    return sumPositionAmounts(holdings.map((h) => h.balance));
}

/** Position uuid → its holding, for payouts and other per-position records. */
export function holdingsByPositionUuid(positions: readonly ClientPositionView[]): Map<string, Holding> {
    const map = new Map<string, Holding>();
    for (const holding of groupHoldings(positions)) {
        for (const uuid of holding.positionUuids) map.set(uuid, holding);
    }
    return map;
}
